/**
 * search-index-loader.js
 *
 * Fetches a prebuilt index JSON and caches it for the life of the page. Serves
 * both artifacts the import script publishes:
 *
 *   search-index.json           every product (~4 MB) — search/collection grids
 *   recommendations-index.json  in-stock only (~180 KB) — PDP carousel
 *
 * Both are static files on the image CDN/S3 with a short Cache-Control, so
 * cross-navigation reuse is handled by the browser's HTTP cache — we
 * deliberately do NOT mirror the payload into sessionStorage to avoid
 * quota/jank; in-memory + HTTP cache gives the same instant feel.
 *
 * Exposes window.SearchIndexLoader.getIndex(url?) -> Promise<Array<row>>.
 * Called with no argument it loads window.SEARCH_INDEX_URL, as before.
 */
(function () {
  // One in-flight/resolved promise per URL, so a page that needs both indexes
  // (or asks twice) fetches each at most once.
  const indexPromises = new Map();

  async function fetchIndex(url) {
    const response = await fetch(url, { cache: 'default' });
    if (!response.ok) {
      throw new Error(`Failed to load index ${url} (${response.status})`);
    }
    return response.json();
  }

  /**
   * Returns the index at `url`, fetching at most once per page load.
   * @param {string} [url] defaults to window.SEARCH_INDEX_URL
   * @returns {Promise<Array<Object>>}
   */
  function getIndex(url = window.SEARCH_INDEX_URL) {
    if (!url) {
      return Promise.reject(new Error('No index URL configured'));
    }

    if (!indexPromises.has(url)) {
      indexPromises.set(
        url,
        fetchIndex(url).catch((error) => {
          // Allow a retry on a later call if the first fetch failed.
          indexPromises.delete(url);
          throw error;
        })
      );
    }
    return indexPromises.get(url);
  }

  window.SearchIndexLoader = { getIndex };
})();
